import { Booking } from '@/types';
import { bookingSegmentsOverlap } from './booking-conflict';

/**
 * Selection logic for resolving an over-utilisation conflict (a resource with
 * 2+ overlapping bookings on the same day).
 *
 * When a conflict is resolved, exactly ONE booking should stay assigned to the
 * original resource (the "anchor") and only the remaining bookings (the
 * "surplus") should be reassigned to other resources.
 *
 * The anchor is chosen deterministically so the behaviour is predictable and
 * testable. In priority order:
 *   1. Prefer a Confirmed booking over an unconfirmed (Planned) one — a
 *      committed booking is less disruptive to leave in place.
 *   2. Prefer the earliest-starting booking.
 *   3. Fall back to the lowest id (lexicographic) as a final tie-break.
 */

/** Normalised view of a booking used purely for anchor selection. */
export interface ResolveCandidate {
  id: string;
  /** Booking start as milliseconds since epoch (0 when unparseable). */
  startMs: number;
  /** True when the booking is confirmed/completed (preferred as anchor). */
  confirmed: boolean;
}

const isConfirmedStatus = (status?: string): boolean =>
  status === 'confirmed' || status === 'completed';

/**
 * Return the id of the booking that should remain with the original resource,
 * or `null` when there are no candidates.
 */
export function selectAnchorId(candidates: ResolveCandidate[]): string | null {
  if (candidates.length === 0) return null;

  return candidates.reduce((best, c) => {
    if (c.confirmed !== best.confirmed) return c.confirmed ? c : best;
    if (c.startMs !== best.startMs) return c.startMs < best.startMs ? c : best;
    return c.id < best.id ? c : best;
  }).id;
}

/**
 * Split a list of conflicting items into the single anchor that stays with the
 * original resource and the surplus that must be reassigned.
 *
 * Generic over the caller's item shape; `toCandidate` maps each item to the
 * fields the anchor rule needs.
 */
export function partitionConflictItems<T>(
  items: T[],
  toCandidate: (item: T) => ResolveCandidate,
): { anchor: T | null; surplus: T[] } {
  const candidates = items.map(toCandidate);
  const anchorId = selectAnchorId(candidates);
  if (anchorId == null) return { anchor: null, surplus: [] };

  let anchor: T | null = null;
  const surplus: T[] = [];
  let anchorTaken = false;

  items.forEach((item, i) => {
    if (!anchorTaken && candidates[i].id === anchorId) {
      anchor = item;
      anchorTaken = true;
    } else {
      surplus.push(item);
    }
  });

  return { anchor, surplus };
}

const bookingStartMs = (startTime: string): number => {
  const t = Date.parse(startTime);
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Convenience wrapper for full {@link Booking} objects (heatmap / calendar
 * resolve paths).
 */
export function partitionConflictBookings(
  bookings: Booking[],
): { anchor: Booking | null; surplus: Booking[] } {
  return partitionConflictItems(bookings, (b) => ({
    id: String(b.id),
    startMs: bookingStartMs(b.startTime),
    confirmed: isConfirmedStatus(b.status),
  }));
}

/**
 * Resolve an over-utilisation from genuinely-overlapping bookings.
 *
 * Unlike {@link partitionConflictBookings} — which treats the whole input as a
 * single conflict and keeps just one booking — this groups the bookings into
 * clusters of genuinely-overlapping same-day bookings (using the time-aware
 * {@link bookingSegmentsOverlap} primitive) and keeps ONE anchor per cluster.
 * A booking that doesn't overlap anything is its own single-item cluster and
 * simply stays put.
 *
 * The returned `surplus` is therefore the minimal-per-cluster set that must be
 * reassigned so no resource-day is double-booked: an over-utilised cell with N
 * overlapping bookings is brought to ≤100% by moving the N−1 surplus while the
 * anchor remains with the original resource. Unrelated non-overlapping
 * bookings (e.g. a multi-day full-day booking on different days) are never
 * swept into the surplus.
 */
export function selectOverlapSurplus(
  bookings: Booking[],
): { anchors: Booking[]; surplus: Booking[] } {
  const n = bookings.length;

  // Union-find over pairwise genuine (time-aware, same-day) overlaps.
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (x: number): number => {
    let root = x;
    while (parent[root] !== root) root = parent[root];
    while (parent[x] !== root) {
      const next = parent[x];
      parent[x] = root;
      x = next;
    }
    return root;
  };
  const union = (a: number, b: number): void => {
    parent[find(a)] = find(b);
  };

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (bookingSegmentsOverlap(bookings[i], bookings[j])) union(i, j);
    }
  }

  const clusters = new Map<number, Booking[]>();
  bookings.forEach((b, i) => {
    const root = find(i);
    const group = clusters.get(root);
    if (group) group.push(b);
    else clusters.set(root, [b]);
  });

  const anchors: Booking[] = [];
  const surplus: Booking[] = [];
  for (const group of clusters.values()) {
    const { anchor, surplus: clusterSurplus } = partitionConflictBookings(group);
    if (anchor) anchors.push(anchor);
    surplus.push(...clusterSurplus);
  }

  return { anchors, surplus };
}
